import React, { useEffect, useState } from 'react';
import { MenuIcon, XIcon, PhoneIcon, MailIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Logo } from './Logo';
import type { Route } from '../hooks/useHashRoute';
type NavbarProps = {
  route: Route;
};
export function Navbar({
  route
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    name: 'Marketing',
    href: '#marketing'
  }, {
    name: 'Design',
    href: '#design'
  }, {
    name: 'CRM',
    href: '#crm'
  }, {
    name: 'Dev',
    href: '#dev'
  }, {
    name: 'Pricing',
    href: '#/pricing'
  }, {
    name: 'Company',
    href: '#company'
  }, {
    name: 'Resources',
    href: '#resources'
  }];
  return <header className={`sticky top-0 z-40 w-full transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-3' : 'bg-white py-5'}`} data-id="element-183" data-mp="emj4ia5fxp4k">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-184" data-mp="jksw6aryi7lf">
        <div className="flex justify-between items-center" data-id="element-185" data-mp="ol8b36rvzn5i">
          {/* Logo */}
          <div className="flex items-center" data-id="element-186" data-mp="3n4fb190xctqc">
            <a href="#/" aria-label="AdROI home" className="flex items-center rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-4" data-id="element-187" data-mp="3n1jadcxozqy7">
              <Logo size="sm" data-id="element-188" data-mp="5n4hjkohfwot" />
            </a>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center space-x-7" data-id="element-189" data-mp="34do38bwl5be2">
            {navLinks.map(link => <a key={link.name} href={link.href} aria-current={link.name === 'Pricing' && route === 'pricing' ? 'page' : undefined} className={`text-sm font-medium transition-colors hover:text-red-600 ${link.name === 'Pricing' && route === 'pricing' ? 'text-red-600' : 'text-gray-600'}`} data-id="element-190" data-mp="39axnze914yvk">
                {link.name}
              </a>)}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-4" data-id="element-191" data-mp="2id6yfir8w4v7">
            <a href="tel:17022134801" className="text-gray-600 hover:text-red-600 transition-colors flex items-center text-sm font-medium" data-id="element-192" data-mp="2ddudj3jxp7v9">
              <PhoneIcon className="h-4 w-4 mr-2" data-id="element-193" data-mp="2nbia7z13opg2" />
              (702) 213-4801
            </a>
            <button className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-md font-semibold text-sm transition-colors shadow-sm hover:shadow-md" data-id="element-194" data-mp="7kci3omtejgs">
              CONTACT US
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center" data-id="element-195" data-mp="a37j1ypt567a">
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-gray-600 hover:text-gray-900 focus:outline-none" aria-label="Toggle menu" data-id="element-196" data-mp="f4eqfwo1g3df">
              {isMobileMenuOpen ? <XIcon className="h-6 w-6" data-id="element-197" data-mp="57s6da3jdgcg" /> : <MenuIcon className="h-6 w-6" data-id="element-198" data-mp="swbg7u28i16n" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-199" data-mp="xw7jfr33xol5">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="lg:hidden bg-white border-t border-gray-100 overflow-hidden" data-id="element-200" data-mp="k26ufp1yjfxz">
            <div className="px-4 pt-2 pb-6 space-y-1 shadow-lg" data-id="element-201" data-mp="3p2ynh7zklaqu">
              {navLinks.map(link => <a key={link.name} href={link.href} className="block px-3 py-3 text-base font-medium text-gray-900 hover:bg-gray-50 hover:text-red-600 rounded-md" onClick={() => setIsMobileMenuOpen(false)} data-id="element-202" data-mp="3k22o97q5ycrr">
                  {link.name}
                </a>)}
              <div className="pt-4 mt-4 border-t border-gray-100 flex flex-col space-y-4" data-id="element-203" data-mp="6o7pga68d60a">
                <a href="tel:17022134801" className="flex items-center px-3 text-base font-medium text-gray-600" data-id="element-204" data-mp="1wzdp7hfh8jy">
                  <PhoneIcon className="h-5 w-5 mr-3 text-gray-400" data-id="element-205" data-mp="3u0rws9w66kvc" />
                  (702) 213-4801
                </a>
                <a href="mailto:Results@GetAdROI.com" className="flex items-center px-3 text-base font-medium text-gray-600" data-id="element-206" data-mp="pmtolji6sn1k">
                  <MailIcon className="h-5 w-5 mr-3 text-gray-400" data-id="element-207" data-mp="28bmtam7fen3l" />
                  Results@GetAdROI.com
                </a>
                <button className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-md font-semibold text-base transition-colors mt-2" data-id="element-208" data-mp="2hahxxtutnp4s">
                  CONTACT US
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}